// composables/useInfiniteScroll.ts
import { ref as vueRef } from 'vue';

/**
 * 무한 스크롤 composable
 * @param fetchCallback - 추가 데이터를 가져오는 콜백 함수
 * @param options - 옵션 설정
 * @returns 스크롤 핸들러 및 상태
 */
export const useInfiniteScroll = (
  fetchCallback: (page: number) => Promise<{ hasMore: boolean; data: any[] }>,
  options: {
    initialPage?: number;
    rowsPerPage?: number;
    scrollMargin?: number;
    alwaysLoadMore?: boolean; // fetchCallback의 hasMore 값을 무시하고 항상 서버 호출
  } = {}
) => {
  const { initialPage = 1, rowsPerPage = 9999, scrollMargin = 10, alwaysLoadMore = false } = options;

  const currentPage = vueRef(initialPage);
  const hasMoreData = vueRef(true);
  const isLoading = vueRef(false);

  /**
   * 페이지 초기화
   */
  const reset = () => {
    currentPage.value = initialPage;
    hasMoreData.value = true;
    isLoading.value = false;
  };

  /**
   * 스크롤 이벤트 핸들러
   */
  const handleScroll = async (event: Event) => {
    const element = event.target as HTMLElement;

    const scrollTop = element.scrollTop;
    const clientHeight = element.clientHeight;
    const scrollHeight = element.scrollHeight;
    const scrollBottom = scrollTop + clientHeight;
    const threshold = scrollHeight - scrollMargin;

    // 스크롤이 하단에 도달했는지 확인
    if (scrollBottom >= threshold) {
      // 이미 로딩 중이거나 더 가져올 데이터가 없으면 요청하지 않음
      if (isLoading.value || !hasMoreData.value) {
        console.log('[useInfiniteScroll] 스크롤 끝 도달 - 스킵', {
          isLoading: isLoading.value,
          hasMoreData: hasMoreData.value
        });
        return;
      }

      console.log('[useInfiniteScroll] 데이터 로딩 시작', {
        currentPage: currentPage.value,
        scrollTop,
        clientHeight,
        scrollHeight,
        scrollBottom,
        threshold
      });

      isLoading.value = true;

      try {
        // 다음 페이지 데이터 요청
        currentPage.value++;

        const result = await fetchCallback(currentPage.value);

        // alwaysLoadMore 옵션이 true이면 hasMore 값을 무시하고 항상 true 유지
        hasMoreData.value = alwaysLoadMore ? true : result.hasMore;

        console.log('[useInfiniteScroll] 데이터 로딩 완료', {
          page: currentPage.value,
          dataLength: result.data.length,
          hasMore: result.hasMore,
          alwaysLoadMore,
          actualHasMoreData: hasMoreData.value
        });
      } catch (error) {
        console.error('무한 스크롤 데이터 로딩 중 오류 발생:', error);
        // 에러 발생 시 페이지 롤백
        currentPage.value--;
      } finally {
        isLoading.value = false;
      }
    }
  };

  return {
    currentPage,
    hasMoreData,
    isLoading,
    handleScroll,
    reset
  };
};
